import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, CSSProperties } from 'react';

import { useDataScrollOverflow, useMergedRef } from '@lib/hooks';
import { cn, cva, type VariantProps } from '@lib/utils';

const scrollShadowVariants = cva([], {
  variants: {
    orientation: {
      vertical: 'overflow-y-auto',
      horizontal: 'overflow-x-auto',
    },
    size: {
      xs: '[--scroll-shadow-size:0.5rem]',
      sm: '[--scroll-shadow-size:1rem]',
      md: '[--scroll-shadow-size:2.5rem]',
      lg: '[--scroll-shadow-size:4rem]',
      xl: '[--scroll-shadow-size:5rem]',
    },
    scrollbar: {
      default: '',
      custom: 'custom-scrollbar',
      hidden: 'hide-scrollbar',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
    size: 'md',
    scrollbar: 'default',
  },
});

type ScrollShadowProps = {
  offset?: number;
  isEnabled?: boolean;
  style?: CSSProperties & {
    '--scroll-shadow-size'?: string;
  };
} & VariantProps<typeof scrollShadowVariants> &
  ComponentPropsWithoutRef<'div'>;

const ScrollShadow = forwardRef<HTMLDivElement, ScrollShadowProps>(
  (
    {
      className,
      children,
      size = 'md',
      offset = 0,
      isEnabled = true,
      orientation = 'vertical',
      scrollbar = 'default',
      style,
      ...rest
    },
    ref
  ) => {
    const effectiveOrientation = orientation ?? 'vertical';

    const { ref: domRef } = useDataScrollOverflow({
      offset,
      isEnabled,
      overflowCheck: effectiveOrientation,
    });

    const mergedRef = useMergedRef(domRef, ref);

    return (
      <div
        ref={mergedRef}
        data-orientation={effectiveOrientation}
        className={cn(
          scrollShadowVariants({ orientation: effectiveOrientation, size, scrollbar }),
          className
        )}
        style={style}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

ScrollShadow.displayName = 'ScrollShadow';

export { ScrollShadow };
export type { ScrollShadowProps };
